import runtimeOperations, {
  EDX_CANONICAL_TENANT_ID,
  isTenantAllowed,
  matchesPathPattern,
  type TenantScope,
} from '@highereducation/dsm-capability-manifest/runtime';
import type { ApiConfig, ApiConfigUrlKey } from '../types/config';

export type AllowedHttpMethod = 'GET' | 'POST';

export type DataSourceService =
  | 'offerings'
  | 'offeringsService'
  | 'organizations'
  | 'deliverySystem'
  | 'campaignManagement'
  | 'mobius'
  | 'reporting'
  | 'sameOrigin'
  | 'productApi';

export { EDX_CANONICAL_TENANT_ID, isTenantAllowed };
export type { TenantScope };

export interface DataSourceDefinition {
  pattern: string;
  methods: readonly AllowedHttpMethod[];
  description: string;
}

function definitionsFor(
  service: DataSourceService,
): readonly DataSourceDefinition[] {
  return runtimeOperations
    .filter((operation) => operation.serviceId === service)
    .flatMap((operation) =>
      [operation.runtimePath, ...(operation.runtimeAliases ?? [])].map(
        (pattern) => ({
          pattern,
          methods: [operation.method],
          description: operation.description,
        }),
      ),
    );
}

export const ALLOWED_DATA_SOURCES: Record<
  DataSourceService,
  readonly DataSourceDefinition[]
> = {
  offerings: definitionsFor('offerings'),
  offeringsService: definitionsFor('offeringsService'),
  organizations: definitionsFor('organizations'),
  deliverySystem: definitionsFor('deliverySystem'),
  campaignManagement: definitionsFor('campaignManagement'),
  mobius: definitionsFor('mobius'),
  reporting: definitionsFor('reporting'),
  sameOrigin: definitionsFor('sameOrigin'),
  productApi: definitionsFor('productApi'),
};

/** Same-origin prefix the edX Product API BFF is mounted at. */
export const PRODUCT_API_BFF_PREFIX = '/api/product-api';

const SERVICE_API_CONFIG_KEY: Record<DataSourceService, ApiConfigUrlKey> = {
  offerings: 'offeringsSearchApiUrl',
  offeringsService: 'offeringsServiceBaseUrl',
  organizations: 'organizationApiUrl',
  deliverySystem: 'deliverySystemApiUrl',
  campaignManagement: 'campaignManagementApiUrl',
  mobius: 'mobiusApiUrl',
  reporting: 'reportingApiUrl',
  sameOrigin: 'dynamicFlowUrl',
  productApi: 'productApiBffOrigin',
};

const SERVICE_API_CONFIG_ALIASES: Partial<
  Record<DataSourceService, ApiConfigUrlKey>
> = {
  offerings: 'offeringsServiceBaseUrl',
  organizations: 'organizationServiceBaseUrl',
  deliverySystem: 'deliverySystemBaseUrl',
};

interface IndexedSource extends DataSourceDefinition {
  service: DataSourceService;
  tenantScope: TenantScope;
}

function isDataSourceService(
  serviceId: string,
): serviceId is DataSourceService {
  return Object.prototype.hasOwnProperty.call(ALLOWED_DATA_SOURCES, serviceId);
}

const FLAT_INDEX: readonly IndexedSource[] = runtimeOperations.flatMap(
  (operation) => {
    const service = operation.serviceId;
    if (!isDataSourceService(service)) return [];
    return [operation.runtimePath, ...(operation.runtimeAliases ?? [])].map(
      (pattern) => ({
        pattern,
        methods: [operation.method],
        description: operation.description,
        service,
        tenantScope: operation.tenantScope,
      }),
    );
  },
);

export class DataSourceConfigurationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'DataSourceConfigurationError';
    Object.setPrototypeOf(this, DataSourceConfigurationError.prototype);
  }
}

function assertWellFormed(source: DataSourceDefinition): void {
  if (!source.pattern.startsWith('/')) {
    throw new DataSourceConfigurationError(
      `Allowlist pattern must start with '/': ${source.pattern}`,
    );
  }
  if (source.pattern.includes('*') && !source.pattern.endsWith('/*')) {
    throw new DataSourceConfigurationError(
      `Allowlist wildcard only permitted as trailing '/*': ${source.pattern}`,
    );
  }
  if (source.methods.length === 0) {
    throw new DataSourceConfigurationError(
      `Allowlist entry has no methods: ${source.pattern}`,
    );
  }
}

for (const source of FLAT_INDEX) assertWellFormed(source);

export class DataSourceNotAllowedError extends Error {
  constructor(
    public readonly service: DataSourceService,
    public readonly path: string,
    public readonly method: string,
    message: string,
  ) {
    super(message);
    this.name = 'DataSourceNotAllowedError';
    Object.setPrototypeOf(this, DataSourceNotAllowedError.prototype);
  }
}

export function isEndpointAllowed(
  endpoint: string,
  method: string,
  tenantId?: string,
): boolean {
  const match = findMatch(endpoint, method);
  return Boolean(match && isTenantAllowed(match.tenantScope, tenantId));
}

export const matchesPattern = matchesPathPattern;

export function isOriginTrusted(
  endpoint: string,
  trustedOrigins: readonly string[] | undefined,
): boolean {
  if (!endpoint) return false;
  if (endpoint.startsWith('/')) return true;

  let parsed: URL;
  try {
    parsed = new URL(endpoint);
  } catch {
    return false;
  }
  if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return false;
  if (!trustedOrigins || trustedOrigins.length === 0) return false;

  const endpointOrigin = parsed.origin.toLowerCase();
  return trustedOrigins.some(
    (trusted) => normalizeTrustedOrigin(trusted) === endpointOrigin,
  );
}

function normalizeTrustedOrigin(value: string): string {
  try {
    return new URL(value).origin.toLowerCase();
  } catch {
    return value.toLowerCase().replace(/\/+$/, '');
  }
}

export function resolveDataSourceUrl(
  service: DataSourceService,
  path: string,
  method: string,
  apiConfig: ApiConfig,
  tenantId?: string,
): string {
  const normalizedPath = extractPath(path);
  const match = normalizedPath
    ? findMatchByNormalizedPath(normalizedPath, method)
    : null;
  if (!match || match.service !== service) {
    throw new DataSourceNotAllowedError(
      service,
      path,
      method,
      `Endpoint ${method} ${path} is not in the data-source allowlist for service '${service}'`,
    );
  }
  if (!isTenantAllowed(match.tenantScope, tenantId)) {
    throw new DataSourceNotAllowedError(
      service,
      path,
      method,
      `Endpoint ${method} ${path} is not allowed for tenant '${tenantId ?? ''}'`,
    );
  }

  const primaryKey = SERVICE_API_CONFIG_KEY[service];
  const aliasKey = SERVICE_API_CONFIG_ALIASES[service];
  const primaryBase = apiConfig[primaryKey];
  const aliasBase = aliasKey ? apiConfig[aliasKey] : undefined;
  const baseUrl =
    service === 'offerings'
      ? (primaryBase ?? aliasBase)
      : (aliasBase ?? primaryBase);
  if (!baseUrl) {
    throw new DataSourceNotAllowedError(
      service,
      path,
      method,
      `No baseUrl configured for service '${service}' (ApiConfig.${primaryKey} is missing)`,
    );
  }

  const joinPath =
    service === 'productApi'
      ? stripProductApiBffPrefix(normalizedPath)
      : normalizedPath;

  return `${baseUrl.replace(/\/$/, '')}${joinPath}`;
}

/**
 * Authored Product API endpoints arrive as the OpenAPI path
 * (`/v1/product-summaries`) or the persisted BFF alias
 * (`/api/product-api/v1/product-summaries`). The origin is that same prefix,
 * so joining the alias unchanged would request the path twice.
 */
function stripProductApiBffPrefix(path: string): string {
  if (
    path !== PRODUCT_API_BFF_PREFIX &&
    !path.startsWith(`${PRODUCT_API_BFF_PREFIX}/`)
  ) {
    return path;
  }
  const stripped = path.slice(PRODUCT_API_BFF_PREFIX.length);
  return stripped.startsWith('/') ? stripped : `/${stripped}`;
}

function findMatch(endpoint: string, method: string): IndexedSource | null {
  const path = extractPath(endpoint);
  return path ? findMatchByNormalizedPath(path, method) : null;
}

function findMatchByNormalizedPath(
  normalizedPath: string,
  method: string,
): IndexedSource | null {
  const normalizedMethod = method.toUpperCase();
  return (
    FLAT_INDEX.find(
      (source) =>
        source.methods.includes(normalizedMethod as AllowedHttpMethod) &&
        matchesPathPattern(normalizedPath, source.pattern),
    ) ?? null
  );
}

function extractPath(endpoint: string): string {
  if (!endpoint || typeof endpoint !== 'string') return '';
  if (containsDotSegment(rawPathPortion(endpoint))) return '';
  if (endpoint.startsWith('/')) return endpoint.split(/[?#]/)[0] ?? '';
  try {
    const parsed = new URL(endpoint);
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return '';
    return parsed.pathname;
  } catch {
    return '';
  }
}

function rawPathPortion(endpoint: string): string {
  if (endpoint.startsWith('/')) return endpoint.split(/[?#]/)[0] ?? '';
  const scheme = /^[a-zA-Z][a-zA-Z0-9+\-.]*:\/\//.exec(endpoint);
  if (!scheme) return endpoint;
  const afterScheme = endpoint.slice(scheme[0].length);
  const pathStart = afterScheme.indexOf('/');
  return pathStart === -1
    ? ''
    : (afterScheme.slice(pathStart).split(/[?#]/)[0] ?? '');
}

function containsDotSegment(path: string): boolean {
  return /(?:^|\/)(?:\.|%2e){1,2}(?:\/|$)/i.test(path);
}
